import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { settingsApi, type CancellationPolicySettings, type CancellationTier } from "@/services/settings";
import { useCan } from "@/auth/permissions";
import { PageShell, LoadingState, ErrorState } from "@/shared/ui/PageHeader";
import { SettingsTabs } from "./SettingsTabs";

const inp =
  "w-full rounded-md border border-slate-300 px-3 py-2 text-[13px] focus:border-blue-500 focus:outline-none";

/**
 * CANCELPOL · 对客取消条款（全公司统一 · tenant_settings.blob['cancellation_policy']）。
 * 风格对齐 ConfirmationTemplateSettingsPage.tsx（同一套设置页范式：阶梯增删 + 保存条）。
 *
 * 🔴 老板口径③：改这里不影响已确认订单——确认时已把当时条款快照进订单，之后怎么改
 * 都不再动那些单。保存成功提示必须写明这一点，防止运营误以为改了就能"追溯生效"。
 */
export function CancellationPolicySettingsPage() {
  const qc = useQueryClient();
  const canEdit = useCan("system.settings.update");

  const q = useQuery({
    queryKey: ["settings", "cancellation-policy"],
    queryFn: settingsApi.getCancellationPolicy,
  });
  const [form, setForm] = useState<CancellationPolicySettings | null>(null);
  useEffect(() => { if (q.data && !form) setForm(q.data); }, [q.data, form]);

  const save = useMutation({
    mutationFn: (p: CancellationPolicySettings) => settingsApi.putCancellationPolicy(p),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["settings", "cancellation-policy"] }),
    onError: (e) => alert((e as Error)?.message || "保存失败，请重试"),
  });

  if (q.isError) return <ErrorState message="取消条款加载失败" onRetry={() => q.refetch()} />;
  if (!form) return <LoadingState />;

  const setTier = (i: number, patch: Partial<CancellationTier>) =>
    setForm({ ...form, tiers: form.tiers.map((t, j) => (j === i ? { ...t, ...patch } : t)) });
  const addTier = () => setForm({ ...form, tiers: [...form.tiers, { days_before: 0, refund_pct: 0 }] });
  const delTier = (i: number) => setForm({ ...form, tiers: form.tiers.filter((_, j) => j !== i) });

  const sortedTiers = [...form.tiers].sort((a, b) => b.days_before - a.days_before);
  const tiersOutOfOrder = JSON.stringify(sortedTiers) !== JSON.stringify(form.tiers) && form.tiers.length > 1;

  return (
    <PageShell title="取消条款" subtitle="全公司统一一套 · 按出发日算天数 · 产品可在产品详情页设专属覆盖">
      <SettingsTabs />

      <section className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between">
          <h2 className="text-[14px] font-bold text-slate-900">退款阶梯（{form.tiers.length} 档）</h2>
          {canEdit && (
            <button onClick={addTier} className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1 text-[12px] font-medium text-slate-600 hover:bg-slate-50">
              <Plus size={13} /> 加一档
            </button>
          )}
        </div>
        <p className="text-[12px] text-slate-500">
          「出发前 N 天（含）以上取消」适用该比例——按天数降序求值，通常最后一档设 0 天兜底。
        </p>
        <div className="flex flex-col gap-2">
          {form.tiers.map((t, i) => (
            <div key={i} className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50/50 p-3">
              <span className="w-5 shrink-0 text-center text-[12px] font-semibold text-slate-500">{i + 1}</span>
              <label className="flex flex-1 items-center gap-2 text-[12px] text-slate-600">
                出发前
                <input type="number" min={0} className={`${inp} w-24`} value={t.days_before} disabled={!canEdit}
                  onChange={(e) => setTier(i, { days_before: Number(e.target.value) })} />
                天（含）以上取消，退款
                <input type="number" min={0} max={100} className={`${inp} w-24`} value={t.refund_pct} disabled={!canEdit}
                  onChange={(e) => setTier(i, { refund_pct: Number(e.target.value) })} />
                %
              </label>
              {canEdit && (
                <button onClick={() => delTier(i)} title="删除本档"
                  className="shrink-0 rounded p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600">
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
          {form.tiers.length === 0 && <p className="py-4 text-center text-[12px] text-slate-400">至少需要一档取消条款。</p>}
          {tiersOutOfOrder && (
            <p className="text-[12px] text-amber-600">⚠ 阶梯未按天数从大到小排列——保存前建议调整顺序，方便他人阅读。</p>
          )}
        </div>
      </section>

      <section className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="text-[14px] font-bold text-slate-900">补充说明（选填）</h2>
        <textarea className={`${inp} h-auto`} rows={3} placeholder="如：不可抗力（自然灾害/政府禁令）不受本条款限制…"
          value={form.note ?? ""} disabled={!canEdit}
          onChange={(e) => setForm({ ...form, note: e.target.value })} />
      </section>

      {canEdit && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <button onClick={() => save.mutate(form)} disabled={save.isPending || form.tiers.length === 0}
              className="h-9 rounded-md bg-blue-600 px-5 text-[13px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
              {save.isPending ? "保存中…" : "保存条款"}
            </button>
            {save.isSuccess && <span className="text-[12px] text-emerald-600">✓ 已保存</span>}
          </div>
          <p className="text-[12px] text-slate-400">
            仅对之后确认的订单生效，已确认订单按其快照条款（改这里不会影响已下单的客人）。
          </p>
        </div>
      )}
    </PageShell>
  );
}

export default CancellationPolicySettingsPage;
